/* This example requires Tailwind CSS v2.0+ */
import { memo } from "react";
import { Disclosure } from "@headlessui/react";
import classnames from "classnames";
import { useLocation } from "react-router-dom";

import { APP_ROUTES } from "const";
import { useMe, useMenuScroll } from "hooks";
import { useActivitiesNotificationOnUpdate } from "./MenuBar.hooks";
import DesktopMenu from "./DesktopMenu";
import MobileMenu from "./MobileMenu";

import type { NavItem } from "./GlobalHeader";

const PRIMARY_NAV_ITEMS: Omit<NavItem, "current">[] = [
  { name: "Issues", href: APP_ROUTES.ISSUES },
  { name: "Solutions", href: APP_ROUTES.SOLUTION_LIST },
  { name: "Action Groups", href: APP_ROUTES.ACTION_GROUP_LIST },
];

const MORE_NAV_ITEMS: Omit<NavItem, "current">[] = [
  { name: "Impact", href: APP_ROUTES.IMPACT },
  { name: "Motivational Contents", href: APP_ROUTES.MOTIVATIONAL_LIST },
  { name: "Handout Sheets", href: APP_ROUTES.HANDOUTS },
];

const MOBILE_ADDITIONAL_ITEMS: Omit<NavItem, "current">[] = [
  { name: "Dashboard", href: APP_ROUTES.DASHBOARD },
];

function useNavigationGroups() {
  const location = useLocation();

  const withState = (items: Omit<NavItem, "current">[]): NavItem[] =>
    items.map((item) => ({
      ...item,
      current:
        location.pathname === item.href ||
        location.pathname.startsWith(item.href + "/"),
    }));

  return {
    primary: withState(PRIMARY_NAV_ITEMS),
    more: withState(MORE_NAV_ITEMS),
    mobile: withState([
      ...PRIMARY_NAV_ITEMS,
      ...MORE_NAV_ITEMS,
      ...MOBILE_ADDITIONAL_ITEMS,
    ]),
  };
}

interface MenuBarProps {
  isAuth?: boolean;
  setPdfUrl?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setHandoutsNoGroupsFound?: React.Dispatch<
    React.SetStateAction<"NoGroups" | "Groups" | "NoSearch">
  >;
}

export function MenuBar({
  isAuth,
  setPdfUrl,
  setHandoutsNoGroupsFound,
}: MenuBarProps) {
  const navigation = useNavigationGroups();
  const { data: profile, isFetching } = useMe({
    enabled: !!isAuth,
    retry: false,
    keepPreviousData: false,
  });
  const isScrolled = useMenuScroll();
  useActivitiesNotificationOnUpdate(!!isAuth);

  return (
    <header className={classnames("z-30 top-0", { sticky: isScrolled })}>
      <Disclosure as="nav" className="relative">
        {({ open, close }) => (
          <div
            className={classnames("w-full", {
              "h-full": open,
              "bg-transparent": !isScrolled,
              "bg-white shadow-lg": isScrolled,
            })}
          >
            <DesktopMenu
              isFetching={isFetching}
              isAuth={isAuth}
              setHandoutsNoGroupsFound={setHandoutsNoGroupsFound}
              setPdfUrl={setPdfUrl}
              primaryNavigation={navigation.primary}
              moreNavigation={navigation.more}
              profile={profile?.data}
              isScrolled={isScrolled}
            />
            <MobileMenu
              isFetching={isFetching}
              isAuth={isAuth}
              open={open}
              navigation={navigation.mobile}
              profile={profile?.data}
              close={close}
              isScrolled={isScrolled}
            />
          </div>
        )}
      </Disclosure>
    </header>
  );
}

export default memo(MenuBar);
